import { useTheme } from "@emotion/react";
import { CorrectAnswerIcon, IncorrectAnswerIcon } from "myplan/components/Icon";
import React, { useContext, useState } from "react";
import { HeadlineRichText } from "../../../../components/RichText";
import Snippet, { SnippetType } from "../../../../components/Snippet";
import Text from "../../../../components/Text";
import { QuestionType } from "../../../../utils/contentful";
import { FurnitureContext } from "../../../../utils/furniture";
import { QuizType } from "../../../../utils/quiz";
import { AppTheme } from "../../../../utils/theme";
import { QuizQuestionComponentProps } from "../../index";
import { Body, Caption, Option } from "../../styles";
import { QuizQuestionMultipleSelectFollowUp } from "../QuizQuestionMultipleSelect";
import {
    FollowUp,
    FollowUpCta,
    KnowledgeTestFollowUp,
    Main,
    MainScroll,
    OptionsSingle as Options,
    ProgressSingle as Progress,
} from "./styles";

type RenderSectionProps = {
    index?: any;
    count?: Number;
    question?: any;
    options?: any[];
    rootResponse?: any;
    next: Function;
    progress?: any;
    isWide?: Boolean;
};

const renderSection = ({
    question,
    options,
    rootResponse,
    next,
    progress,
    isWide,
}: RenderSectionProps) => {
    // handle multi-select followups
    if (question.type === QuestionType.MultipleSelect) {
        return (
            <QuizQuestionMultipleSelectFollowUp
                question={question}
                options={options}
                next={next}
                progress={progress}
                isWide={isWide}
            />
        );
    }
    const {
        color: { selected },
    }: AppTheme = useTheme() as AppTheme;
    return (
        <>
            <Progress type="caption1">{progress}</Progress>
            <Body type="h8">
                <HeadlineRichText doc={question.bodyRich} />
            </Body>
            {question.captionRich && (
                <Caption type="caption1">
                    <HeadlineRichText doc={question.captionRich} />
                </Caption>
            )}
            <Options isWide={isWide}>
                {options
                    .filter(option => option)
                    .map(option => {
                        const isSelected = rootResponse
                            ? rootResponse.optionIds.includes(option._id)
                            : false;
                        const textType = isSelected ? "cta3" : "cta2";

                        return (
                            <Option
                                key={option._id}
                                onPress={() =>
                                    !rootResponse &&
                                    next(question._id, option._id)
                                }
                                underlayColor={selected}
                                isSelected={isSelected}
                                isWide={isWide}>
                                <OptionBody
                                    body={option.body}
                                    bodyRich={option.bodyRich}
                                    textType={textType}
                                />
                            </Option>
                        );
                    })}
            </Options>
        </>
    );
};

/**
 * Default Options and Options have slightly different signatures
 * Default Options have a 'body' while Optoins have rich text body
 * This flips betwixt
 * @returns
 */
const OptionBody = ({ bodyRich, body, textType }) => {
    let theBody = bodyRich ? <HeadlineRichText doc={bodyRich} /> : body;
    return (
        <Text
            type={textType}
            allowFontScaling={false}
            style={{
                textAlign: "center",
                width: "100%",
                // borderColor: "white",
                // borderWidth: 1,
            }}>
            {theBody}
        </Text>
    );
};

const renderKnowledgeTestFollowUp = ({
    Icon,
    title,
    body,
    next,
    rootResponse,
    ctaLabel,
}) => {
    return (
        <KnowledgeTestFollowUp>
            <MainScroll>
                <Snippet
                    type={SnippetType.KnowledgeTestResult}
                    title={title}
                    body={body}
                    Icon={Icon}
                />
                <FollowUpCta
                    title={ctaLabel}
                    onPress={() =>
                        next(rootResponse.questionId, rootResponse.optionId)
                    }
                />
            </MainScroll>
        </KnowledgeTestFollowUp>
    );
};

const QuizQuestionSingleSelect = ({
    question,
    options,
    index,
    count,
    next,
    getOptions,
    quizType,
    isWide = false,
}: QuizQuestionComponentProps) => {
    const [followUp, setFollowUp] = useState(null);
    const [rootResponse, setRootResponse] = useState(null);
    const {
        strings: { nextCta, followUpCta },
    } = useContext(FurnitureContext);
    const progress = `${index + 1} of ${count}`;

    const _next = (
        questionId?: string,
        optionId?: string,
        optionIds?: string[],
    ) => {
        if (questionId === question._id) {
            if (
                quizType === QuizType.KnowledgeTest ||
                quizType === QuizType.SimpleChecklist
            ) {
                if (!followUp && question.correctResultBody) {
                    const correctMatch = question.correctAnswers.find(
                        correctAnswer => correctAnswer._id === optionId,
                    );
                    let _followUp;

                    if (correctMatch) {
                        _followUp = {
                            title: question.correctResultTitle,
                            body: question.correctResultBody,
                        };

                        if (quizType === QuizType.KnowledgeTest) {
                            _followUp.Icon = CorrectAnswerIcon;
                        }
                    } else {
                        _followUp = {
                            title: question.incorrectResultTitle,
                            body: question.incorrectResultBody,
                        };

                        if (quizType === QuizType.KnowledgeTest) {
                            _followUp.Icon = IncorrectAnswerIcon;
                        }
                    }

                    setRootResponse({ questionId, optionIds: [optionId] });
                    setFollowUp(_followUp);
                } else {
                    const _rootResponse = rootResponse || {
                        questionId,
                        optionIds: [optionId],
                    };

                    next([_rootResponse]);
                }
            } else {
                const followUpOutcome = question.outcomes?.find(
                    outcome => outcome.type === "FOLLOW_UP",
                );

                if (!followUpOutcome) {
                    const response = { questionId, optionIds: [optionId] };
                    next([response]);
                } else {
                    const shouldTrigger = followUpOutcome.triggers.reduce(
                        (acc, trigger) => {
                            if (acc) {
                                return acc;
                            }

                            return trigger.option._id === optionId;
                        },
                        false,
                    );

                    if (!shouldTrigger) {
                        const response = { questionId, optionId };
                        setRootResponse(null);
                        setFollowUp(null);
                        next([response]);
                    } else {
                        const followUpOptions = getOptions(
                            null,
                            followUpOutcome.followUpQuestion,
                        );
                        const _followUp = {
                            question: followUpOutcome.followUpQuestion,
                            options: followUpOptions,
                        };

                        setRootResponse({ questionId, optionIds: [optionId] });
                        setFollowUp(_followUp);
                    }
                }
            }
        } else if (questionId === followUp.question._id) {
            // if the follow up is a multi-select, handle multiple options
            let _optionIds = Array.isArray(optionIds) ? optionIds : [optionId];
            const responses = [
                rootResponse,
                {
                    questionId,
                    optionIds: _optionIds,
                    question: followUp.question,
                },
            ];

            next(responses);
        }
    };

    return (
        <>
            <Main>
                {renderSection({
                    next: _next,
                    index,
                    count,
                    question,
                    options,
                    rootResponse,
                    progress,
                    isWide,
                })}
            </Main>
            {quizType !== QuizType.KnowledgeTest &&
                quizType !== QuizType.SimpleChecklist &&
                followUp && (
                    <FollowUp>
                        {renderSection({
                            next: _next,
                            index,
                            count,
                            question: followUp.question,
                            options: followUp.options,
                            progress: followUpCta,
                        })}
                    </FollowUp>
                )}
            {(quizType === QuizType.KnowledgeTest ||
                quizType === QuizType.SimpleChecklist) &&
                followUp && (
                    <FollowUp>
                        {renderKnowledgeTestFollowUp({
                            title: followUp.title,
                            body: followUp.body,
                            Icon: followUp.Icon,
                            rootResponse,
                            next: _next,
                            ctaLabel: nextCta,
                        })}
                    </FollowUp>
                )}
        </>
    );
};

export default QuizQuestionSingleSelect;
